import { reportErrorToPosthog } from "@fideus/analytics";
import { createAuthressAuthProvider, SignedUpGuard } from "@fideus/auth";
import { ConsoleLogger } from "@fideus/logger";
import {
  ErrorBoundary,
  LightThemeProvider,
  LoadingPage,
} from "@fideus-ui/core";
import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { createPosthogAnalyticsProvider, initPosthog } from "./_app/analytics";
import type { Event } from "./_app/analytics/events/Event";
import { TrpcApiProvider } from "./_app/api";
import { ComposedProvider } from "./_app/ComposedProvider";
import { ConfigProvider, getConfig, initConfig } from "./_app/config";
import { createPosthogFeatureFlagProvider } from "./_app/featureFlags";
import { i18n, i18nInitPromise } from "./_app/i18n";
import { App } from "./App";

async function init() {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  await initConfig();
  await i18nInitPromise;

  const config = getConfig();
  let posthog = null;

  if (!config.postHog && !import.meta.env.DEV) {
    throw new Error("PostHog config is not set");
  }

  if (config.postHog) {
    posthog = initPosthog(config.postHog);
  }

  const providers = await Promise.all([
    ConfigProvider,
    LightThemeProvider,
    createPosthogAnalyticsProvider<Event>({
      posthog,
      application: config.name,
      applicationVersion: config.version ?? "unknown",
      userType: "taxAdvisor",
    }),
    createPosthogFeatureFlagProvider({
      posthog,
      flags: config.featureFlags,
    }),
    createAuthressAuthProvider({
      authressApiUrl: config.loginUrl,
      authressApplicationId: config.authressApplicationId,
      loadingFallback: <LoadingPage loadingText="Login wird vorbereitet..." />,
      audiences: [config.apiUrl],
    }),
  ]);

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <ErrorBoundary onError={reportErrorToPosthog}>
          <ComposedProvider providers={providers}>
            <SignedUpGuard
              loadingFallback={
                <LoadingPage loadingText="Login wird vorbereitet..." />
              }
            >
              <TrpcApiProvider apiUrl={config.apiUrl}>
                <App />
              </TrpcApiProvider>
            </SignedUpGuard>
          </ComposedProvider>
        </ErrorBoundary>
      </I18nextProvider>
    </React.StrictMode>
  );
}

void init().catch((error) => {
  const logger = new ConsoleLogger();
  logger.info("Initialization error:", error);
});
